import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import { useT } from '../i18n/useT';

const STAT_ICONS = [
  <VerifiedRoundedIcon sx={{ color: '#0F172A', fontSize: '1.5rem' }} />,
  <ApartmentRoundedIcon sx={{ color: '#0F172A', fontSize: '1.5rem' }} />,
  <PublicRoundedIcon sx={{ color: '#0F172A', fontSize: '1.5rem' }} />,
  <GavelRoundedIcon sx={{ color: '#0F172A', fontSize: '1.5rem' }} />,
];

const CustomersSection: React.FC = () => {
  const { t, r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  const credibility = r.customers.credibility;

  return (
    <Box
      component="section"
      id="customers"
      aria-labelledby="customers-heading"
      sx={{
        py: { xs: 7, md: 10 },
        backgroundColor: '#F8FAFC',
        borderBottom: '1px solid #E2E8F0',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              borderRadius: 4,
              backgroundColor: 'white',
              border: '1px solid #E2E8F0',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              overflow: 'hidden',
            }}
          >
            {/* HERO ROW */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 0.9fr) 1.4fr' },
                gap: { xs: 4, md: 6 },
                px: { xs: 3, md: 6 },
                pt: { xs: 4, md: 6 },
                pb: { xs: 4, md: 5 },
              }}
            >
              {/* LEFT — number block */}
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.4,
                    py: 0.5,
                    borderRadius: 999,
                    backgroundColor: '#0F172A',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    mb: 2.5,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#22C55E',
                      boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.22)',
                    }}
                    aria-hidden
                  />
                  {t('customers.eyebrow')}
                </Box>
                <Typography
                  component="p"
                  sx={{
                    fontSize: { xs: '5.2rem', sm: '6rem', md: '7.6rem' },
                    fontWeight: 900,
                    lineHeight: 0.95,
                    letterSpacing: '-0.05em',
                    background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {t('customers.number')}
                </Typography>
                <Typography
                  component="h2"
                  id="customers-heading"
                  sx={{
                    color: '#0F172A',
                    fontWeight: 800,
                    mt: 1.5,
                    fontSize: { xs: '1.15rem', md: '1.35rem' },
                    lineHeight: 1.25,
                    letterSpacing: '-0.015em',
                    maxWidth: { md: 340 },
                  }}
                >
                  {t('customers.title')}
                </Typography>
              </Box>

              {/* RIGHT — narrative */}
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#334155',
                    lineHeight: 1.65,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    mb: 3,
                  }}
                >
                  {t('customers.subtitle')}
                </Typography>

                <Box
                  sx={{
                    position: 'relative',
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 2.5,
                    backgroundColor: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    borderLeft: '4px solid #0F172A',
                  }}
                >
                  <FormatQuoteRoundedIcon
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 12,
                      color: '#CBD5E1',
                      fontSize: '1.6rem',
                      transform: 'scaleX(-1)',
                    }}
                    aria-hidden
                  />
                  <Typography
                    sx={{
                      color: '#0F172A',
                      fontWeight: 600,
                      fontSize: { xs: '0.97rem', md: '1.02rem' },
                      lineHeight: 1.5,
                      pr: 3,
                    }}
                  >
                    {t('customers.reinforcement')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* STATS ROW */}
            <Box
              sx={{
                borderTop: '1px solid #E2E8F0',
                backgroundColor: '#FAFBFC',
                px: { xs: 2, md: 4 },
                py: { xs: 2, md: 2.5 },
              }}
            >
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  m: 0,
                  p: 0,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    md: `repeat(${credibility.length}, 1fr)`,
                  },
                  gap: { xs: 1.5, md: 1 },
                }}
              >
                {credibility.map((item, idx) => (
                  <Box
                    component="li"
                    key={item}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.2,
                      px: { xs: 1.5, md: 2 },
                      py: { xs: 1.2, md: 1.4 },
                      borderRadius: 1.5,
                      backgroundColor: 'white',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.2,
                        backgroundColor: '#F1F5F9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                      aria-hidden
                    >
                      {STAT_ICONS[idx % STAT_ICONS.length]}
                    </Box>
                    <Typography
                      sx={{
                        color: '#0F172A',
                        fontWeight: 700,
                        fontSize: { xs: '0.82rem', md: '0.9rem' },
                        letterSpacing: '-0.005em',
                        lineHeight: 1.2,
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CustomersSection;
